import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ShieldCheck, AlertCircle, Clock, Upload } from "lucide-react";
import twilioService from '../../services/twilioService';

type Country = 'CH' | 'AT';

/** Prefill values carried over from the onboarding company step (step 2). */
export interface RegulatoryDefaults {
  businessName?: string;
  email?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  /** tax_id from step 2 — for CH this is the UID (CHE-...). */
  vatId?: string;
  /** contact_person from step 2 — AT prefill for the authorized representative. */
  contactPerson?: string;
}

interface RegulatoryBundleFormProps {
  country: Country;
  numberType?: 'local' | 'mobile';
  /** Prefill from already-entered company data so nothing is typed twice. */
  defaults?: RegulatoryDefaults;
  /** Called once the bundle reaches twilio-approved. */
  onApproved: () => void;
}

// What each country's Twilio regulation actually accepts (mirrors the backend
// regulatory_policy.py, verified against the live Regulations API):
// CH: ONE Handelsregisterauszug (commercial_registrar_excerpt) + UID on the company.
// AT: Firmenbuchauszug + Lichtbildausweis der vertretungsberechtigten Person.
const COUNTRY_META: Record<Country, {
  label: string;
  regionLabel: string;
  city: string;
  primaryDocLabel: string;
  companyIdLabel: string | null;
  companyIdPlaceholder: string;
  needsRepresentative: boolean;
}> = {
  CH: {
    label: 'Schweiz',
    regionLabel: 'Kanton',
    city: 'Bern',
    primaryDocLabel: 'Handelsregisterauszug',
    companyIdLabel: 'UID / Unternehmens-ID',
    companyIdPlaceholder: 'CHE-123.456.789',
    needsRepresentative: false,
  },
  AT: {
    label: 'Österreich',
    regionLabel: 'Bundesland',
    city: 'Wien',
    primaryDocLabel: 'Firmenbuchauszug',
    companyIdLabel: null,
    companyIdPlaceholder: '',
    needsRepresentative: true,
  },
};

const PRIMARY_DOC_TYPE = 'commercial_registrar_excerpt';

// Twilio's Address "Region" field is free text (no ISO code required — the
// account has approved bundles with full names like "Zürich"/"Salzburg").
// The dropdown standardizes on full names so the address always matches the
// register document. DE is included for the day the central-bundle path gets
// replaced by self-service; the form itself currently only renders CH/AT.
const REGIONS: Record<'DE' | 'CH' | 'AT', string[]> = {
  DE: [
    'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
    'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen',
    'Rheinland-Pfalz', 'Saarland', 'Sachsen', 'Sachsen-Anhalt',
    'Schleswig-Holstein', 'Thüringen',
  ],
  AT: [
    'Burgenland', 'Kärnten', 'Niederösterreich', 'Oberösterreich', 'Salzburg',
    'Steiermark', 'Tirol', 'Vorarlberg', 'Wien',
  ],
  CH: [
    'Aargau', 'Appenzell Ausserrhoden', 'Appenzell Innerrhoden', 'Basel-Landschaft',
    'Basel-Stadt', 'Bern', 'Freiburg', 'Genf', 'Glarus', 'Graubünden', 'Jura',
    'Luzern', 'Neuenburg', 'Nidwalden', 'Obwalden', 'Schaffhausen', 'Schwyz',
    'Solothurn', 'St. Gallen', 'Tessin', 'Thurgau', 'Uri', 'Waadt', 'Wallis',
    'Zug', 'Zürich',
  ],
};

// AT second document: proof of the authorized representative.
// Twilio's manual review requires a VISIBLE ADDRESS on this document. Austrian
// passports and ID cards never show a residential address, so those uploads get
// rejected ("Proof of Identity does not show a visible address" — 7x across our
// customers). A bank confirmation / statement shows name + address and is the
// reliable option, so it leads the list and is the default.
const ID_DOC_TYPES = [
  { value: 'bank_statement', label: 'Bankbestätigung / Kontoauszug – mit Name + Adresse (empfohlen)' },
  { value: 'passport', label: 'Reisepass (zeigt keine Adresse – wird oft abgelehnt)' },
  { value: 'government_issued_document', label: 'Amtlicher Lichtbildausweis, z. B. Personalausweis (zeigt keine Adresse – wird oft abgelehnt)' },
];

// Twilio bundle statuses that mean "under review — poll".
const PENDING_STATUSES = new Set(['pending-review', 'in-review', 'provisionally-approved']);

/** Strip invisible copy-paste artifacts (soft hyphen, zero-widths, BOM, NBSP)
 *  — Twilio matches these strings EXACTLY against the uploaded documents. */
const clean = (value: string): string =>
  value
    .replace(/[\u00ad\u200b\u200c\u200d\ufeff]/g, '')
    .replace(/\u00a0/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

/** Best-effort "Vorname Nachname" split for the AT representative prefill. */
const splitName = (full?: string): { first: string; last: string } => {
  const parts = clean(full ?? '').split(' ');
  if (parts.length < 2) return { first: parts[0] ?? '', last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
};

const RegulatoryBundleForm: React.FC<RegulatoryBundleFormProps> = ({ country, numberType = 'local', defaults, onApproved }) => {
  const meta = COUNTRY_META[country];

  const [phase, setPhase] = useState<'loading' | 'form' | 'review' | 'rejected'>('loading');
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Concrete, per-document reasons from Twilio's review (German, via backend).
  const [rejectionReasons, setRejectionReasons] = useState<string[]>([]);

  // Form fields — prefilled from the company step (step 2) so nothing is typed twice.
  const [email, setEmail] = useState(defaults?.email ?? '');
  const [businessName, setBusinessName] = useState(defaults?.businessName ?? '');
  const [street, setStreet] = useState(defaults?.street ?? '');
  const [city, setCity] = useState(defaults?.city ?? '');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState(defaults?.postalCode ?? '');
  // CH: UID (enterprise_id_number) — prefilled from the step-2 tax id.
  const [companyId, setCompanyId] = useState(defaults?.vatId ?? '');
  // AT: authorized representative — prefilled from the step-2 contact person.
  const prefillName = splitName(defaults?.contactPerson);
  const [repFirstName, setRepFirstName] = useState(prefillName.first);
  const [repLastName, setRepLastName] = useState(prefillName.last);
  const prefilled = Boolean(defaults?.businessName || defaults?.street || defaults?.postalCode);

  const [documentNumber, setDocumentNumber] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [idDocType, setIdDocType] = useState(ID_DOC_TYPES[0].value);
  const [idFile, setIdFile] = useState<File | null>(null);

  const approvedRef = useRef(false);

  const applyStatus = (s: string | null, hasBundle: boolean, approved?: boolean) => {
    setStatus(s);
    if (approved || s === 'twilio-approved') {
      if (!approvedRef.current) {
        approvedRef.current = true;
        onApproved();
      }
      setPhase('review');
    } else if (s === 'twilio-rejected') {
      setPhase('rejected');
    } else if (hasBundle && s && PENDING_STATUSES.has(s)) {
      setPhase('review');
    } else {
      setPhase('form');
    }
  };

  const refreshStatus = async () => {
    try {
      const res = await twilioService.getRegulatoryStatus(country, numberType);
      setRejectionReasons(res.rejection_reasons ?? []);
      applyStatus(res.status, res.has_bundle, res.approved);
    } catch {
      setPhase('form');
    }
  };

  useEffect(() => {
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, numberType]);

  // Poll while under review.
  useEffect(() => {
    if (phase !== 'review' || approvedRef.current) return;
    const id = setInterval(refreshStatus, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const canSubmit = useMemo(() => {
    const base = Boolean(
      email.trim() && businessName.trim() && street.trim() && city.trim() && postalCode.trim() && file,
    );
    if (!base) return false;
    if (country === 'CH') return Boolean(companyId.trim());
    return Boolean(repFirstName.trim() && repLastName.trim() && idFile);
  }, [country, email, businessName, street, city, postalCode, file, companyId, repFirstName, repLastName, idFile]);

  const handleSubmit = async () => {
    if (!canSubmit || !file) return;
    setSubmitting(true);
    setError(null);
    try {
      const cleanedBusinessName = clean(businessName);
      const endUserAttributes: Record<string, string> =
        country === 'CH'
          ? { business_name: cleanedBusinessName, enterprise_id_number: clean(companyId) }
          : { business_name: cleanedBusinessName, first_name: clean(repFirstName), last_name: clean(repLastName) };

      const form = new FormData();
      form.set('country', country);
      form.set('number_type', numberType);
      form.set('end_user_type', 'business');
      form.set('email', email.trim());
      form.set('friendly_name', cleanedBusinessName);
      form.set('end_user_attributes', JSON.stringify(endUserAttributes));
      form.set('address', JSON.stringify({
        customer_name: cleanedBusinessName,
        street: clean(street),
        city: clean(city),
        region: clean(region),
        postal_code: clean(postalCode),
      }));
      form.set('document_type', PRIMARY_DOC_TYPE);
      form.set('document_attributes', JSON.stringify({
        document_number: clean(documentNumber),
        business_name: cleanedBusinessName,
      }));
      form.set('document_file', file);
      if (country === 'AT' && idFile) {
        form.set('document2_type', idDocType);
        form.set('document2_attributes', JSON.stringify({
          first_name: clean(repFirstName),
          last_name: clean(repLastName),
        }));
        form.set('document2_file', idFile);
      }

      const res = await twilioService.submitRegulatoryBundle(form);
      setRejectionReasons([]);
      applyStatus(res.status, true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Einreichung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'loading') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="h-4 w-4 animate-spin" /> Regulatorik-Status wird geprüft…
      </div>
    );
  }

  if (phase === 'review') {
    const approved = status === 'twilio-approved';
    return (
      <Alert className={approved ? 'border-success/25 bg-success/10' : 'border-warning/25 bg-warning/10'}>
        {approved ? <ShieldCheck className="h-4 w-4 text-success-strong" /> : <Clock className="h-4 w-4 text-warning-strong" />}
        <AlertDescription className="text-muted-foreground">
          {approved ? (
            <>Regulatorik für {meta.label} <b>freigegeben</b> – du kannst jetzt eine {meta.city}-Nummer wählen.</>
          ) : (
            <>Deine Angaben für {meta.label} liegen bei Twilio <b>in Prüfung</b> (Status: {status}). Das kann einige Stunden bis Tage dauern – wir prüfen automatisch weiter.</>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="border-[#1967D2]/30">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[#1967D2]" />
          Regulatorik {meta.label} ({meta.city})
        </CardTitle>
        <CardDescription>
          {country === 'CH' ? (
            <>Twilio verlangt für eine Schweiz-Rufnummer die UID deines Unternehmens und einen
            Handelsregisterauszug (Firmenname + Adresse müssen exakt mit dem Auszug übereinstimmen).</>
          ) : (
            <>Twilio verlangt für eine Österreich-Rufnummer einen Firmenbuchauszug sowie Name und
            einen Nachweis der vertretungsberechtigten Person <b>mit sichtbarer Adresse</b>
            {' '}(empfohlen: Bankbestätigung/Kontoauszug – Pässe und Personalausweise zeigen keine
            Adresse und werden häufig abgelehnt).</>
          )}{' '}
          Nach dem Absenden prüft Twilio die Angaben (asynchron); danach kannst du eine Nummer kaufen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {phase === 'rejected' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">Twilio hat die vorherige Einreichung abgelehnt:</p>
              {rejectionReasons.length > 0 ? (
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  {rejectionReasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1">Bitte Angaben und Dokumente prüfen und erneut senden.</p>
              )}
              <p className="mt-2 text-xs">
                Nach der Korrektur bitte alle Dokumente erneut hochladen und absenden – die Prüfung startet dann neu.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {prefilled && (
          <p className="text-xs text-muted-foreground">
            Aus deinen Unternehmensdaten (Schritt 2) vorausgefüllt – bitte prüfen und ggf. ergänzen.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Firmenname (wie im {meta.primaryDocLabel})</Label>
            <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Muster Salon GmbH" />
            <p className="text-xs text-muted-foreground">
              Nur für die Behördenprüfung: buchstabengetreu wie im Register eingetragen –
              inklusive Rechtsform (z. B. „e.U.“, „GmbH“). <b>Dein Assistent nennt am Telefon und
              in WhatsApp weiterhin deinen gewohnten Namen</b> – dieses Feld ändert daran nichts.
            </p>
          </div>
          {meta.companyIdLabel && (
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{meta.companyIdLabel}</Label>
              <Input value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder={meta.companyIdPlaceholder} />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Kontakt-E-Mail</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="inhaber@salon.ch" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Straße & Nr.</Label>
            <Input value={street} onChange={(e) => setStreet(e.target.value)} placeholder="Bahnhofstrasse 1" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">PLZ</Label>
            <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="3011" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ort</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder={meta.city} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{meta.regionLabel}</Label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full h-10 rounded-md border border-border bg-muted px-3 text-sm"
            >
              <option value="">Bitte wählen…</option>
              {REGIONS[country].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>

        {meta.needsRepresentative && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground md:col-span-2">
              Vertretungsberechtigte Person (muss mit dem Ausweisdokument übereinstimmen):
            </p>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vorname</Label>
              <Input value={repFirstName} onChange={(e) => setRepFirstName(e.target.value)} placeholder="Max" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nachname</Label>
              <Input value={repLastName} onChange={(e) => setRepLastName(e.target.value)} placeholder="Mustermann" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dokument 1: {meta.primaryDocLabel}</Label>
            <p className="text-xs text-muted-foreground">
              Muss Firmenname und Adresse exakt wie oben angegeben zeigen – buchstabengetreu wie
              im Register eingetragen. Der Auszug darf höchstens 12 Monate alt sein.
            </p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Registernummer (optional)</Label>
            <Input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} placeholder={country === 'CH' ? 'CHE-123.456.789' : 'FN 123456a'} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Datei (JPEG, PNG oder PDF, max. 5 MB)</Label>
            <div className="flex items-center gap-3">
              <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
        </div>

        {meta.needsRepresentative && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dokument 2: Nachweis der vertretungsberechtigten Person</Label>
              <select
                value={idDocType}
                onChange={(e) => setIdDocType(e.target.value)}
                className="w-full h-10 rounded-md border border-border bg-muted px-3 text-sm"
              >
                {ID_DOC_TYPES.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
              {idDocType === 'bank_statement' ? (
                <p className="text-xs text-muted-foreground">
                  Muss <b>Name und Adresse</b> der vertretungsberechtigten Person zeigen und
                  höchstens <b>3 Monate</b> alt sein. Empfohlen, weil Twilio auf dem Ausweis eine
                  sichtbare Adresse verlangt – österreichische Pässe/Personalausweise zeigen keine.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  <b>Achtung:</b> Twilio verlangt eine sichtbare Adresse auf dem Ausweis;
                  österreichische Pässe und Personalausweise zeigen keine und werden daher häufig
                  abgelehnt. Falls du dennoch einen Ausweis nutzt: Vorder- <b>und</b> Rückseite in
                  <b> einer</b> Datei hochladen (z. B. beide Seiten in einem PDF).
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Datei (JPEG, PNG oder PDF, max. 5 MB)</Label>
              <div className="flex items-center gap-3">
                <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={(e) => setIdFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive-strong flex items-center gap-1">
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting} className="bg-[#1967D2] hover:bg-[#1558b0]">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Zur Prüfung an Twilio senden
          </Button>
          {!canSubmit && <span className="text-xs text-muted-foreground">Bitte alle Pflichtfelder + Dokument{meta.needsRepresentative ? 'e' : ''} ausfüllen.</span>}
        </div>
      </CardContent>
    </Card>
  );
};

export default RegulatoryBundleForm;
